// src/pages/AdminPanel.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AdminPanel.css";
import { useNavigate } from "react-router-dom";

const API_URL =
  process.env.REACT_APP_API_URL?.trim() ||
  "https://us-central1-ottomun-87f07.cloudfunctions.net/api";

const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("conferences");
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("adminLoggedIn");
    if (isLoggedIn !== "true") {
      window.location.replace("/admin-login");
    } else {
      setAuthorized(true);
    }
  }, []);

  if (!authorized) return null;

  const handleLogout = () => {
    localStorage.removeItem("adminLoggedIn");
    alert("🔒 Logged out successfully!");
    navigate("/admin-login");
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <button className="logout-btn" onClick={handleLogout}>
          🚪 Logout
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === "conferences" ? "active" : ""}
          onClick={() => setActiveTab("conferences")}
        >
          Conferences
        </button>
        <button
          className={activeTab === "academic" ? "active" : ""}
          onClick={() => setActiveTab("academic")}
        >
          Academic Team
        </button>
      </div>

      {activeTab === "conferences" && <ConferenceManager />}
      {activeTab === "academic" && <AcademicTeamManager />}
    </div>
  );
};

/* =======================================================
   🗓️ CONFERENCE MANAGER (Committees Removed)
======================================================= */
const ConferenceManager = () => {
  const [form, setForm] = useState({
    title: "",
    date: "",
    location: "",
    description: "",
    image: "",
    about: "",
    codeOfConduct: "",
  });
  const [conferences, setConferences] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const fetchConferences = async () => {
    try {
      const res = await axios.get(`${API_URL}/conferences`);
      setConferences(res.data);
    } catch (err) {
      console.error("❌ Error fetching conferences:", err);
    }
  };

  useEffect(() => {
    fetchConferences();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/conferences/${editingId}`, form);
        alert("✅ Conference updated!");
      } else {
        await axios.post(`${API_URL}/conferences`, form);
        alert("✅ Conference added!");
      }
      resetForm();
      fetchConferences();
    } catch (err) {
      console.error("❌ Error saving conference:", err);
      alert(`❌ ${err.response?.data?.error || err.message}`);
    }
  };

  const handleEdit = (conf) => {
    setForm(conf);
    setEditingId(conf.id || conf._id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API_URL}/conferences/${id}`);
      alert("🗑️ Conference deleted!");
      fetchConferences();
    } catch (err) {
      console.error("❌ Error deleting conference:", err);
    }
  };

  const resetForm = () =>
    setForm({
      title: "",
      date: "",
      location: "",
      description: "",
      image: "",
      about: "",
      codeOfConduct: "",
    });

  return (
    <div>
      <h2>🗓️ Manage Conferences</h2>

      <form onSubmit={handleSubmit} className="admin-form">
        <label>Title</label>
        <input name="title" value={form.title} onChange={handleChange} />

        <label>Date</label>
        <input name="date" value={form.date} onChange={handleChange} />

        <label>Location</label>
        <input name="location" value={form.location} onChange={handleChange} />

        <label>Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
        />

        <label>Image URL</label>
        <input name="image" value={form.image} onChange={handleChange} />

        <label>About</label>
        <textarea name="about" value={form.about} onChange={handleChange} />

        <label>Code of Conduct</label>
        <div
          contentEditable
          className="rich-text-editor"
          dangerouslySetInnerHTML={{ __html: form.codeOfConduct }}
          onInput={(e) =>
            setForm({ ...form, codeOfConduct: e.currentTarget.innerHTML })
          }
        />

        <button type="submit" className="submit-btn">
          {editingId ? "💾 Update Conference" : "🚀 Add Conference"}
        </button>
      </form>

      <div className="conference-list">
        <h2>📋 Existing Conferences</h2>
        {conferences.map((conf) => (
          <div key={conf.id} className="conference-item">
            {conf.image && <img src={conf.image} alt="" className="thumb" />}
            <div>
              <h3>{conf.title}</h3>
              <p>
                {conf.date} — {conf.location}
              </p>
            </div>
            <div className="actions">
              <button onClick={() => handleEdit(conf)}>✏️</button>
              <button onClick={() => handleDelete(conf.id)}>🗑️</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* =======================================================
   🎓 ACADEMIC TEAM MANAGER (UNCHANGED)
======================================================= */
const AcademicTeamManager = () => {
  const [team, setTeam] = useState([]);
  const [newMember, setNewMember] = useState({
    name: "",
    role: "",
    bio: "",
    image: null,
  });
  const [uploading, setUploading] = useState(false);

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API_URL}/academic-team`);
      setTeam(res.data);
    } catch (err) {
      console.error("❌ Error fetching team:", err);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!newMember.name || !newMember.role) {
      alert("⚠️ Please fill in both name and role.");
      return;
    }

    const formData = new FormData();
    formData.append("name", newMember.name);
    formData.append("role", newMember.role);
    formData.append("bio", newMember.bio);
    if (newMember.image) formData.append("image", newMember.image);

  try {
      setUploading(true);
      await axios.post(`${API_URL}/academic-team`, formData);
      alert("✅ Member added successfully!");
      setNewMember({ name: "", role: "", bio: "", image: null });
      fetchTeam();
    } catch (err) {
      console.error("❌ Error adding member:", err.response?.data || err.message);
      alert(`❌ Failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this member?")) return;
    try {
      await axios.delete(`${API_URL}/academic-team/${id}`);
      fetchTeam();
      alert("🗑️ Deleted successfully!");
    } catch (err) {
      console.error("❌ Error deleting member:", err);
      alert(`❌ Failed: ${err.response?.data?.error || err.message}`);
    }
  };

  return (
    <div>
      <h2>🎓 Manage Academic Team</h2>
      <form onSubmit={handleAdd} className="admin-form">
        <label>Name</label>
        <input
          type="text"
          value={newMember.name}
          onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
        />

        <label>Role</label>
        <input
          type="text"
          value={newMember.role}
          onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
        />

        <label>Bio</label>
        <textarea
          rows={3}
          value={newMember.bio}
          onChange={(e) => setNewMember({ ...newMember, bio: e.target.value })}
        />

        <label>Image</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) =>
            setNewMember({ ...newMember, image: e.target.files[0] })
          }
        />

        <button type="submit" disabled={uploading}>
          {uploading ? "⏳ Uploading..." : "➕ Add Member"}
        </button>
      </form>

      <div className="academic-list">
        <h3>📋 Current Academic Team</h3>
        {team.length === 0 ? (
          <p>No team members found.</p>
        ) : (
          <FlipCardExtension team={team} handleDelete={handleDelete} />
        )}
      </div>
    </div>
  );
};

const FlipCardExtension = ({ team, handleDelete }) => {
  const [flipped, setFlipped] = useState(null);

  return (
    <div className="team-grid flip-extension">
      {team.map((member, index) => (
        <div
          key={member.id}
          className={`team-card ${flipped === index ? "flipped" : ""}`}
          onClick={() => setFlipped(flipped === index ? null : index)}
        >
          <div className="card-inner">
            <div className="card-front">
              {member.image && (
                <img src={member.image} alt={member.name} className="team-photo" />
              )}
              <h4>{member.name}</h4>
              <p>
                <strong>{member.role}</strong>
              </p>
            </div>
            <div className="card-back">
              <p>{member.bio || "No additional info."}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(member.id);
                }}
              >
                🗑️ Remove
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminPanel;
